import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus, Search } from "lucide-react";
import React, { useContext, useState, useTransition } from "react";
import AuthContext from "@/store/contexts/AuthContext";
import { toast } from "sonner";
import axios from "@/services/axios";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { Category } from "@/type/common";

const NewPageDialog = ({ createText }: { createText?: string }) => {
  const { pages, setPages, categories, loadCategories, user } = useContext(AuthContext);
  const [loading, startTransition] = useTransition();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const { t } = useTranslation();

  const selectedCategory = categories.find((cat) => cat.id.toString() === categoryId);

  // Filter categories based on search
  const filteredCategories = searchQuery
    ? categories.filter((cat) =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : categories;

  const handleCategoryChange = (selectedValue: string) => {
    if (selectedValue === "create") {
      setIsCreatingCategory(true);
    } else {
      setCategoryId(selectedValue);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error(t("home.categoryNameRequired"));
      return;
    }

    try {
      const response = await axios.post("/odash/categories/create", {
        name: newCategoryName.trim(),
      });

      if (response.data) {
        const newCategory: Category = response.data;
        toast.success(t("home.categoryCreated"));
        
        await loadCategories();
        
        setCategoryId(newCategory.id.toString());
        setIsCreatingCategory(false);
        setNewCategoryName("");
        setSearchQuery("");
      }
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error(t("home.categoryCreateError"));
    }
  };

  const handleCancelCreateCategory = () => {
    setIsCreatingCategory(false);
    setNewCategoryName("");
  };

  const handleManagePlan = () => {
    // Get the O'Dashboard portal URL from environment
    const portalUrl = import.meta.env.VITE_OSOLUTION_API || 'https://odashboard.app';
    // Get the license key from localStorage
    const licenseKey = localStorage.getItem('odash_license_key') || '';
    // Navigate to the manage plan page
    const managePlanUrl = `${portalUrl}/odash/manage-plan?key=${licenseKey}`;
    window.open(managePlanUrl, '_blank');
  };

  const handleNewPage = async () => {
    if (pages.length >= 1 && user.plan === "freemium") {
      toast.error(
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }}>
          <div>{t("editor.navbar.subscriptionRequired")}</div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleManagePlan();
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#000',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            {t("editor.navbar.managePlan")}
          </button>
        </div>,
        {
          style: {
            maxWidth: '500px',
          },
        }
      );
      return;
    }
    startTransition(async () => {
      const pageData: any = {
        title,
        icon: "layout-dashboard",
        content: null,
        id: uuidv4(),
        root: { props: { title, icon: "layout-dashboard" } },
      };
      
      // Add category_id if selected
      if (categoryId && categoryId !== "none") {
        pageData.category_id = parseInt(categoryId);
      }
      
      const newPage = await axios
        .post("/odash/pages", pageData)
        .then((e) => e.data)
        .catch((_e) => null);
      if (!newPage) return;
      setPages((prevPages: any) => [...prevPages, newPage]);
      navigate(`/${newPage.id}`);
      setOpen(false);
      setTitle("");
      setCategoryId("");
      setSearchQuery("");
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size={createText ? "lg" : "icon"}>
          <Plus />
          {createText}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("home.newPage")}</DialogTitle>
          <DialogDescription>{t("home.newPageDescription")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="page-title">{t("home.pageTitle")}</Label>
            <Input
              id="page-title"
              placeholder={t("home.newPagePlaceholder")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          
          {!isCreatingCategory && (
            <div className="space-y-2">
              <Label htmlFor="page-category">{t("home.categoryOptional")}</Label>
              <Select value={categoryId || "none"} onValueChange={handleCategoryChange}>
                <SelectTrigger id="page-category" className="w-full">
                  <SelectValue placeholder={t("home.selectCategoryPlaceholder")}>
                    {categoryId && selectedCategory ? selectedCategory.name : t("home.noCategory")}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-96 overflow-hidden">
                  {/* Search Input - only show if there are categories */}
                  {categories.length > 0 && (
                    <div className="px-2 py-2 sticky top-0 bg-background z-10 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder={t("home.searchCategories")}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="h-8 pl-8"
                        />
                      </div>
                    </div>
                  )}

                  <div className="overflow-y-auto max-h-80 px-1 py-1">
                    {/* Create New Category Button */}
                    <SelectItem value="create" className="flex items-center gap-2 font-medium text-primary">
                      <span className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        {t("home.createNewCategory")}
                      </span>
                    </SelectItem>

                    {/* No Category Option */}
                    <SelectItem value="none">{t("home.noCategory")}</SelectItem>

                    {/* Filtered Categories */}
                    {filteredCategories.length > 0 ? (
                      filteredCategories.map((category) => (
                        <SelectItem key={category.id} value={category.id.toString()}>
                          {category.name}
                        </SelectItem>
                      ))
                    ) : categories.length > 0 ? (
                      <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                        {t("home.noCategoriesFound")}
                      </div>
                    ) : null}
                  </div>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Create Category Form */}
          {isCreatingCategory && (
            <div className="space-y-2">
              <Label>{t("home.newCategory")}</Label>
              <div className="space-y-2">
                <Input
                  placeholder={t("home.categoryNamePlaceholder")}
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCreateCategory();
                    } else if (e.key === "Escape") {
                      handleCancelCreateCategory();
                    }
                  }}
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleCreateCategory}
                    disabled={!newCategoryName.trim()}
                  >
                    {t("home.create")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelCreateCategory}>
                    {t("home.cancel")}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("home.cancel")}</Button>
          </DialogClose>
          <Button onClick={() => handleNewPage()} disabled={!title.trim()}>
            {t("home.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewPageDialog;
